import { BaseEdge, getBezierPath, type EdgeProps } from '@xyflow/react'
import { memo, useEffect, useRef, useState } from 'react'
import EventsEngine from '../../../engines/EventsEngine'
import { IFlowAnimation, IFlowAnimationGroup } from '../../../definitions/FlowAnimation'
import Helper from '../../../helpers/Helper'
import FlowUIManager from '../../../managers/FlowUIManager'

const CustomEdgeUI = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition }: EdgeProps) => {
    const [animation, setAnimation] = useState<IFlowAnimation>(null)
    const pathRef = useRef<SVGPathElement>(null)

    const pathLength = pathRef.current?.getTotalLength() ?? 0
    const isAnimationExecuting = animation && animation.animation === 'executing'
    const isAnimationFailed = animation && animation.animation === 'failed'

    const xEqual = sourceX === targetX
    const yEqual = sourceY === targetY

    const [edgePath] = getBezierPath({
        sourceX: xEqual ? sourceX + 0.0001 : sourceX,
        sourceY: yEqual ? sourceY + 0.0001 : sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition
    })

    useEffect(() => {
        EventsEngine.subscribe('flow-animation', 'CustomEdgeUI' + id, (args: IFlowAnimationGroup) => {
            const animationNode = args?.animations.find(x => x.edgeId === id)
            if (animationNode && animationNode.animation && animationNode.animation.length > 0) {
                // it's a piece of crap, I need it to give me a new value every time the animation is updated, the way to update the key of the svg, otherwise when I start the first flow the edje are animated normally then the second time, since they have the same key they are not reanimated
                setAnimation({ ...animationNode, uuid: Helper.generateUUID('edge_animation') })
            }
        })

        EventsEngine.subscribe('query-cache-stale', 'CustomEdgeUI' + id, () => {
            setAnimation(null)
        })

        return () => {
            EventsEngine.unsubscribe('flow-animation', 'CustomEdgeUI' + id)
            EventsEngine.unsubscribe('query-cache-stale', 'CustomEdgeUI' + id)
        }
    }, [id])

    return (
        <>
            <BaseEdge id={id} path={edgePath} />
            <svg key={animation?.uuid}>
                <path
                    ref={pathRef}
                    d={edgePath}
                    stroke={isAnimationExecuting ? '#4DB6AC' : isAnimationFailed ? 'var(--color-bad)' : undefined}
                    fill='none'
                    className='path'
                    strokeDasharray={pathLength}
                    strokeDashoffset={pathLength}>
                    {(isAnimationExecuting) && (
                        <animate
                            attributeName='stroke-dashoffset'
                            to='0'
                            dur={`${FlowUIManager.EDGE_ANIMATION_DURATION_MS}ms`}
                            begin='0ms'
                            end={`${FlowUIManager.EDGE_ANIMATION_DURATION_MS}ms`}
                            fill='freeze'
                        />
                    )}
                </path>
                {isAnimationExecuting && (
                    <circle r='6' fill='#4DB6AC' style={{ filter: 'drop-shadow(0 0 4px rgba(77, 182, 172, 1))' }} visibility='hidden'>
                        <animate
                            attributeName='visibility'
                            from='hidden'
                            to='visible'
                            begin='0ms'
                            dur='100ms'
                            fill='freeze'
                        />
                        <animateMotion
                            key={edgePath + id + animation.animation + animation.uuid}
                            dur={`${FlowUIManager.EDGE_ANIMATION_DURATION_MS}ms`}
                            path={edgePath}
                        />
                        <animate
                            attributeName='opacity'
                            from='1'
                            to='0'
                            begin={`${FlowUIManager.EDGE_ANIMATION_DURATION_MS - 1}ms`}
                            dur='1ms'
                            fill='freeze'
                        />
                    </circle>
                )}
            </svg>
        </>
    )
}

export default memo(CustomEdgeUI)
